import { Flex, Text } from "@chakra-ui/react";
import { useContext, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router";

import { ProjectRead } from "@nens/rana/api-v1";
import { RanaLoading, RanaModal } from "@nens/ranalib";
import { TenantContext } from "@nens/ranalib";
import { RanaForm } from "@nens/ranalib";

import { cancelButtonText, typeHereString } from "@/translations";

import { useDeleteProject } from "../api/delete-projects";

interface ProjectDeleteModalProps {
  project: ProjectRead;
  onClose: () => void;
}

export function ProjectDeleteModal({ project, onClose }: ProjectDeleteModalProps) {
  const intl = useIntl();
  const { tenant } = useContext(TenantContext);
  const navigate = useNavigate();
  const { isMutating, mutate } = useDeleteProject(project.id, () => {
    tenant && navigate(`/${tenant}/projects`);
    onClose();
  });
  const deleteProjects = () => mutate();
  const [canDelete, setCanDelete] = useState(false);

  return (
    <RanaModal
      isOpen
      onClose={onClose}
      topBar={<FormattedMessage id="header.delete_project" defaultMessage="Delete project" />}
      maxW="xl"
      maxH="fit"
      buttons={[
        {
          children: cancelButtonText,
          variant: "tertiary",
          isDisabled: isMutating,
          onClick: onClose,
        },
        {
          children: <FormattedMessage id="button.delete" defaultMessage="Delete" />,
          variant: "danger",
          isDisabled: isMutating || !canDelete,
          onClick: deleteProjects,
        },
      ]}
    >
      <Flex flexDir="column" gap={4}>
        <Text>
          <FormattedMessage
            id="message.do_you_want_to_delete_project"
            defaultMessage="Do you really want to delete this project? This action {cannot} be undone. This will permanently delete the {projectName} project.
            All files, ran processes and publications will be deleted."
            values={{
              cannot: (
                <Text as="strong">
                  <FormattedMessage id="cannot" defaultMessage="CANNOT" />
                </Text>
              ),
              projectName: <Text as="strong">{project.name} </Text>,
            }}
          />
        </Text>

        <RanaForm initialValues={{ confirmation: "" }}>
          <RanaForm.Input
            onChange={(e) => {
              setCanDelete(e.target.value === project.name);
            }}
            label={intl.formatMessage({
              id: "message.type_below_to_delete",
              defaultMessage: "Please type in the name of the project to confirm",
            })}
            explanation={typeHereString(intl)}
            autoFocus
            field="confirmation"
          />
          <RanaForm.Errors />
        </RanaForm>
        {isMutating && <RanaLoading size="md" isCentered />}
      </Flex>
    </RanaModal>
  );
}
